import type { BaseUnit, IngredientId, Measure, MixingState, Recipe } from '../types';

// Volumetric units normalize to teaspoons; count-style units compare directly.
// A unit-kind mismatch (cups of eggs) scores 0 for that ingredient.
const TSP_PER: Partial<Record<BaseUnit, number>> = {
  tsp: 1,
  tbsp: 3,
  cup: 48,
};

const FRACTION_VALUE: Record<string, number> = {
  '1/4': 0.25,
  '1/3': 1 / 3,
  '1/2': 0.5,
  '2/3': 2 / 3,
  '3/4': 0.75,
};

export function measureValue(m: Measure): number {
  return m.qty + (m.fraction ? FRACTION_VALUE[m.fraction] : 0);
}

function isVolumetric(unit: BaseUnit): boolean {
  return unit in TSP_PER;
}

// Normalize an amount to a comparable magnitude: teaspoons for volumetric
// units, raw count otherwise. Returns null when kinds are incompatible.
function normalize(qty: number, unit: BaseUnit, target: BaseUnit): number | null {
  if (isVolumetric(unit) && isVolumetric(target)) return qty * TSP_PER[unit]!;
  if (unit === target) return qty;
  return null;
}

export interface MixReport {
  accuracy: number; // 0..1
  perIngredient: { ingredient: IngredientId; added: number; target: number; score: number }[];
  extras: IngredientId[];
}

export function computeMixAccuracy(mix: MixingState, recipe: Recipe): MixReport {
  // Sum everything added per ingredient, normalized against that ingredient's
  // recipe target unit. Incompatible-unit adds poison the ingredient (null).
  const perIngredient: MixReport['perIngredient'] = [];
  const addedByIngredient = new Map<IngredientId, number | null>();

  for (const add of mix.adds) {
    const target = recipe.ingredients.find((r) => r.ingredient === add.ingredient);
    const targetUnit = target?.measure.unit ?? add.unit;
    const norm = normalize(add.qty, add.unit, targetUnit);
    const prev = addedByIngredient.get(add.ingredient);
    if (norm === null || prev === null) {
      addedByIngredient.set(add.ingredient, null);
    } else {
      addedByIngredient.set(add.ingredient, (prev ?? 0) + norm);
    }
  }

  let sum = 0;
  for (const r of recipe.ingredients) {
    const targetNorm = isVolumetric(r.measure.unit)
      ? measureValue(r.measure) * TSP_PER[r.measure.unit]!
      : measureValue(r.measure);
    const added = addedByIngredient.get(r.ingredient);
    const addedNorm = added ?? 0;
    const score = added === null || targetNorm === 0
      ? 0
      : Math.max(0, 1 - Math.abs(addedNorm - targetNorm) / targetNorm);
    perIngredient.push({ ingredient: r.ingredient, added: addedNorm, target: targetNorm, score });
    sum += score;
  }

  const needed = new Set(recipe.ingredients.map((r) => r.ingredient));
  const extras = [...addedByIngredient.keys()].filter((id) => !needed.has(id));
  const mean = recipe.ingredients.length > 0 ? sum / recipe.ingredients.length : 0;
  const accuracy = Math.max(0, Math.min(1, mean - extras.length * 0.1));

  return { accuracy, perIngredient, extras };
}

// Bowl fill fraction: total volume added vs the recipe's total, for the
// Kitchen's bowl visual. Count units weigh in at their tsp-ish equivalents
// so eggs visibly move the needle.
export function bowlFillFraction(mix: MixingState, recipe: Recipe): number {
  const volume = (qty: number, unit: BaseUnit): number => qty * (TSP_PER[unit] ?? 9);
  const added = mix.adds.reduce((s, a) => s + volume(a.qty, a.unit), 0);
  const target = recipe.ingredients.reduce((s, r) => s + volume(measureValue(r.measure), r.measure.unit), 0);
  if (target === 0) return 0;
  return Math.min(1, added / target);
}

export type QualityGrade = 'perfect' | 'good' | 'okay' | 'bad';

export function qualityGrade(mixAccuracy: number, doneness: string | undefined): QualityGrade {
  if (doneness === 'raw' || doneness === 'burnt') return 'bad';
  const early = doneness === 'baking';
  if (mixAccuracy >= 0.9) return early ? 'good' : 'perfect';
  if (mixAccuracy >= 0.7) return early ? 'okay' : 'good';
  if (mixAccuracy >= 0.5) return 'okay';
  return 'bad';
}
